import { useEffect, useState, type ReactNode } from 'react';
import type { OperacionesClienteFinal } from '../api/ops/clienteFinal';
import type { MiCita, VistaPreviaMiCancelacion } from '../api/ops/citasCliente';
import { ErrorApi } from '../api/problema';
import { Chip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import { dinero } from '../logica/dinero';
import { diaCorto, diaLargo, mayuscula, nombreServicios, RUTA_MIS_CITAS } from './citas';
import { useCancelacion } from './cancelacion';
import { DetalleCita, VentanaCancelar } from './CitaCliente';
import { Baldosa, BotonPc, Pagina } from './Pagina';

/**
 * El enlace de la cita (9.10, `/cita/{business_id}/{token}`, el `enlace_cita` de la confirmación): anónimo, sin
 * identificarse. Se ve la cita (`getAppointmentByLink`) y se cancela con el importe a la vista (`cancelAppointmentByLink`);
 * para cambiarla, «Mis citas» con un código (G35). Vale hasta el inicio de la cita: después, «Este enlace ya no funciona».
 */
interface Props {
    ops: OperacionesClienteFinal;
    negocio: string;
    token: string;
    navegar: (ruta: string, reemplazar?: boolean) => void;
}

type Estado = { tipo: 'cargando' } | { tipo: 'error' } | { tipo: 'caducado' } | { tipo: 'ok'; cita: MiCita } | { tipo: 'cancelada-ahora'; cita: MiCita; vista: VistaPreviaMiCancelacion };

export function EnlaceCita({ ops, negocio, token, navegar }: Props) {
    const [estado, setEstado] = useState<Estado>({ tipo: 'cargando' });
    const [intento, setIntento] = useState(0);

    useEffect(() => {
        let vivo = true;
        ops.getAppointmentByLink(negocio, token)
            .then((cita) => { if (vivo) setEstado({ tipo: 'ok', cita }); })
            .catch((e: unknown) => { if (vivo) setEstado(e instanceof ErrorApi && (e.estado === 404 || e.estado === 410) ? { tipo: 'caducado' } : { tipo: 'error' }); });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, token, intento]);

    const cita = estado.tipo === 'ok' || estado.tipo === 'cancelada-ahora' ? estado.cita : null;
    const cancelacion = useCancelacion({
        leer: () => (cita?.cancellation_preview != null ? Promise.resolve(cita.cancellation_preview) : ops.getAppointmentByLink(negocio, token).then((c) => c.cancellation_preview ?? Promise.reject(new Error('sin vista previa')))),
        cancelar: (c) => ops.cancelAppointmentByLink(negocio, token, c),
        onHecha: (c, vista) => { setEstado({ tipo: 'cancelada-ahora', cita: c, vista }); },
    });

    const nombre = cita?.business_name ?? '';
    const pagina = (contenido: ReactNode, centrada = false) => (
        <Pagina cabecera={nombre === '' ? { tipo: 'cuenta' } : { tipo: 'negocio', nombre, linea: 'Tu cita' }} pie={{ tipo: 'reservaria' }} centrada={centrada}>
            {contenido}
        </Pagina>
    );
    const aMisCitas = () => { navegar(RUTA_MIS_CITAS); };

    if (estado.tipo === 'cargando') {
        return pagina(<section className="rv-pc-card" aria-busy="true" aria-label="Cargando"><div className="rv-pc-esqueleto" style={{ height: 160 }} /></section>);
    }
    if (estado.tipo === 'error') {
        return pagina(
            <section className="rv-pc-card">
                <Baldosa icono="triangle-alert" fondo="var(--rv-red-soft)" color="var(--rv-red)" />
                <div className="rv-pc-card__cab"><h1>No hemos podido abrir tu cita</h1><span>Puede ser la conexión. No se ha perdido nada. Prueba de nuevo.</span></div>
                <BotonPc texto="Reintentar" icono="refresh-cw" onClick={() => { setEstado({ tipo: 'cargando' }); setIntento((i) => i + 1); }} />
            </section>,
            true,
        );
    }
    if (estado.tipo === 'caducado') {
        return pagina(
            <section className="rv-pc-card">
                <div className="rv-pc-baldosa-fila"><Baldosa icono="link" fondo="var(--rv-paper)" color="var(--rv-ink)" /><Chip estado="problem">Enlace no válido</Chip></div>
                <div className="rv-pc-card__cab"><h1>Este enlace ya no funciona</h1><span>Los enlaces de la cita valen hasta la hora de la cita. Si todavía tienes una cita próxima, la encuentras en «Mis citas».</span></div>
                <BotonPc texto="Ir a Mis citas" icono="arrow-right" onClick={aMisCitas} />
            </section>,
            true,
        );
    }
    if (estado.tipo === 'cancelada-ahora') {
        const c = estado.cita;
        return pagina(
            <section className="rv-pc-card">
                <div className="rv-pc-baldosa-fila"><Baldosa icono="check" fondo="var(--rv-red)" color="#fff" /><Chip estado="problem">Cancelada</Chip></div>
                <div className="rv-pc-card__cab">
                    <h1>Hemos cancelado tu cita</h1>
                    <span>{estado.vista.refund.amount > 0 ? `Te devolvemos ${dinero(estado.vista.refund)}. Puede tardar unos días en verse en tu cuenta.` : 'La hora ha quedado libre para otra persona.'}</span>
                </div>
                <div className="rv-ct-filas">
                    <div><span>Era</span><span>{`${mayuscula(diaCorto(c.local_date))}, ${c.local_time}`}</span></div>
                    <div><span>Servicios</span><span>{nombreServicios(c)}</span></div>
                    <div><span>Código</span><span>{c.code}</span></div>
                </div>
                <p className="rv-pc-nota"><Icono nombre="message-circle" /><span>Te mandamos la confirmación.</span></p>
            </section>,
            true,
        );
    }
    const c = estado.cita;
    if (c.status === 'cancelled') {
        return pagina(
            <section className="rv-pc-card">
                <div className="rv-pc-baldosa-fila"><Baldosa icono="calendar-x" fondo="var(--rv-paper)" color="var(--rv-ink)" /><Chip estado="problem">Cancelada</Chip></div>
                <div className="rv-pc-card__cab"><h1>Esta cita ya está cancelada</h1><span>{`La cita del ${diaLargo(c.local_date)} a las ${c.local_time} está cancelada. No tienes que hacer nada más.`}</span></div>
                <BotonPc tipo="sec" texto="Ver mis citas" icono="calendar" onClick={aMisCitas} />
            </section>,
            true,
        );
    }
    return pagina(
        <>
            <DetalleCita
                cita={c}
                porEnlace
                negocio={nombre === '' ? 'el negocio' : nombre}
                banda={null}
                onCambiar={() => { navegar(`${RUTA_MIS_CITAS}?negocio=${encodeURIComponent(negocio)}&cita=${encodeURIComponent(c.id)}&vista=cambiar`); }}
                onCancelar={cancelacion.abrir}
                onFirmar={() => undefined}
                onPedirOtra={null}
            />
            <VentanaCancelar
                abierta={cancelacion.abierta}
                cita={c}
                vista={cancelacion.vista}
                cambiada={cancelacion.cambiada}
                error={cancelacion.error}
                nota={cancelacion.nota}
                onNota={cancelacion.setNota}
                ocupado={cancelacion.ocupado}
                onCancelar={cancelacion.confirmar}
                onCerrar={cancelacion.cerrar}
            />
        </>,
    );
}
